import { type ModuleRoute } from "@/types/core";

/**
 * Module route configurations
 */
export const MODULE_ROUTES: Record<string, ModuleRoute[]> = {
  admin: [
    { path: "/", label: "Overview" },
    { path: "/overview", label: "Overview" },
    { path: "/applications", label: "Applications" },
    {
      path: "/applications/professional/:professionalId",
      label: "Professional Application"
    },
    {
      path: "/applications/practice/:practiceId",
      label: "Practice Application"
    },
    { path: "/users", label: "Users" },
    { path: "/users/:userId", label: "User Details" },
    { path: "/practices", label: "Practices" },
    { path: "/professionals", label: "Professionals" },
    { path: "/payments", label: "Payments" },
    { path: "/fees", label: "Shift Fees" },
    { path: "/subscription-invoices", label: "Subscription Invoices" },
    { path: "/catalogue", label: "Catalogue" },
    { path: "/pricing", label: "Pricing" },
    { path: "/shifts", label: "Shifts" },
    { path: "/tiers", label: "Loyalty" },
    { path: "/disputes", label: "Disputes" },
    { path: "/ratings", label: "Ratings" },
    { path: "/notifications", label: "Notifications" },
    { path: "/help", label: "Help" },
    { path: "/search", label: "Search" }
  ],
  practice: [
    { path: "/", label: "Overview" },
    { path: "/overview", label: "Overview" },
    { path: "/loyality", label: "Loyality" },
    { path: "/subscription", label: "Subscription" },
    { path: "/shifts", label: "Shifts" },
    { path: "/shifts/post", label: "Post Shift" },
    { path: "/shifts/templates", label: "Shift templates" },
    { path: "/shifts/:shiftId/edit", label: "Edit Shift" },
    { path: "/shifts/:shiftId", label: "Shift Details" },
    { path: "/payments", label: "Payments" },
    { path: "/verification", label: "Verification" },
    { path: "/messaging", label: "Messaging" },
    { path: "/notifications", label: "Notifications" },
    { path: "/profile", label: "Profile" },
    { path: "/settings", label: "Settings" },
    { path: "/help", label: "Help" },
    { path: "/search", label: "Search" }
  ],
  professional: [
    { path: "/", label: "Overview" },
    { path: "/overview", label: "Overview" },
    { path: "/loyality", label: "Loyality" },
    { path: "/referrals", label: "Referrals" },
    { path: "/shifts", label: "Shifts" },
    {
      path: "/shifts/opportunity/:shiftId",
      label: "Shift opportunity"
    },
    { path: "/shifts/:shiftId", label: "Shift Details" },
    { path: "/payments", label: "Earnings" },
    { path: "/verification", label: "Verification" },
    { path: "/messaging", label: "Messaging" },
    { path: "/notifications", label: "Notifications" },
    { path: "/profile", label: "Profile" },
    { path: "/settings", label: "Settings" },
    { path: "/help", label: "Help" },
    { path: "/search", label: "Search" }
  ]
};

/**
 * Get routes for a specific module
 */
export function getModuleRoutes(moduleName: string): ModuleRoute[] {
  return MODULE_ROUTES[moduleName] || [];
}

/**
 * Check if a route is valid for a module
 */
export function isValidModuleRoute(moduleName: string, path: string): boolean {
  const routes = getModuleRoutes(moduleName);
  return routes.some((route) => matchRoutePath(route.path, path));
}

/**
 * Whether a module-relative path is somewhere a link can usefully go.
 *
 * Stricter than {@link isValidModuleRoute}, and the difference is the point: a `:param`
 * matches any literal. `/shifts/:shiftId` happily matches `/shifts/opportunity`, so a
 * breadcrumb built from `/professional/shifts/opportunity/{id}` would offer a link to the
 * shift viewer with `shiftId="opportunity"` — a page that cannot load.
 *
 * A template matches *honestly* when no `:param` position swallows a segment that some
 * template uses as a static segment at that same position. `123` is nobody's static
 * segment, so `/shifts/123` links; `opportunity` is static in
 * `/shifts/opportunity/:shiftId`, so `/shifts/opportunity` does not.
 */
export function isNavigableModulePath(
  moduleName: string,
  path: string
): boolean {
  const segments = path.split("/").filter(Boolean);
  const templates = getModuleRoutes(moduleName).map((route) =>
    route.path.split("/").filter(Boolean)
  );

  const isStaticElsewhere = (segment: string, index: number) =>
    templates.some((template) => template[index] === segment);

  return templates.some((template) => {
    if (template[template.length - 1] === "*") {
      // A splat is a catch-all, not a page of its own.
      return false;
    }
    if (template.length !== segments.length) {
      return false;
    }

    return template.every((part, index) =>
      part.startsWith(":")
        ? !isStaticElsewhere(segments[index], index)
        : part === segments[index]
    );
  });
}

/**
 * Get the default route for a module
 */
export function getModuleDefaultRoute(moduleName: string): string {
  const routes = getModuleRoutes(moduleName);
  const defaultRoute = routes.find(
    (route) => route.path === "/" || route.path === "/overview"
  );
  return defaultRoute?.path || "/";
}

/**
 * Create a full path for a module route
 */
export function createModulePath(
  moduleName: string,
  routePath: string
): string {
  // Remove leading slash from routePath to avoid double slashes
  const cleanRoutePath = routePath.startsWith("/")
    ? routePath.slice(1)
    : routePath;
  return `/${moduleName}/${cleanRoutePath}`;
}

/**
 * Extract module name and route from a full path
 */
export function parseModulePath(
  fullPath: string
): { moduleName: string; routePath: string } | null {
  const pathParts = fullPath.split("/").filter(Boolean);

  if (pathParts.length === 0) {
    return null;
  }

  const moduleName = pathParts[0];
  const routePath =
    pathParts.length > 1 ? `/${pathParts.slice(1).join("/")}` : "/";

  return { moduleName, routePath };
}

/**
 * Check if an actual path matches a route template supporting ":param" segments
 */
export function matchRoutePath(
  routeTemplate: string,
  actualPath: string
): boolean {
  // Normalize leading/trailing slashes
  const normalize = (p: string) =>
    (p.startsWith("/") ? p : `/${p}`).replace(/\/$/, "");
  const a = normalize(routeTemplate).split("/").filter(Boolean);
  const b = normalize(actualPath).split("/").filter(Boolean);

  // Support simple wildcard at end
  const hasSplat = a[a.length - 1] === "*";
  if (!hasSplat && a.length !== b.length) return false;
  if (hasSplat && b.length < a.length - 1) return false;

  const len = hasSplat ? a.length - 1 : a.length;
  for (let i = 0; i < len; i++) {
    const segA = a[i];
    const segB = b[i];
    if (!segA) return false;
    if (segA.startsWith(":")) continue; // dynamic segment
    if (segA !== segB) return false;
  }
  return true;
}

/**
 * Extract params from a path based on a route template with ":param" segments
 */
export function extractRouteParams(
  routeTemplate: string | undefined,
  actualPath: string | undefined
): Record<string, string> {
  if (!routeTemplate || !actualPath) return {};

  const normalize = (p: string) =>
    (p.startsWith("/") ? p : `/${p}`).replace(/\/$/, "");
  const a = normalize(routeTemplate).split("/").filter(Boolean);
  const b = normalize(actualPath).split("/").filter(Boolean);
  const params: Record<string, string> = {};
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const segA = a[i];
    const segB = b[i];
    if (segA.startsWith(":")) {
      params[segA.slice(1)] = decodeURIComponent(segB);
    }
  }
  return params;
}
